import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../config';

const GoogleTracking = () => {
  const location = useLocation();
  const [tracking, setTracking] = useState(null);
  const [cookieConsent, setCookieConsent] = useState(null);

  // Paginile admin NU se urmăresc deloc în GA4/GTM (date curate: doar vizitatori reali)
  const isAdminPage = location.pathname.startsWith('/admin');

  // Fetch tracking settings din API
  useEffect(() => {
    const fetchTracking = async () => {
      try {
        const response = await fetch(`${API_URL}/api/orders/tracking`);
        const data = await response.json();
        setTracking(data);
      } catch (error) {
        console.error('Error fetching tracking settings:', error);
      }
    };

    fetchTracking();
  }, []);

  // Citește cookie consent din localStorage
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      try {
        const parsed = JSON.parse(consent);
        setCookieConsent(parsed);
      } catch (error) {
        // Backwards compatibility: format vechi era string simplu "accepted"/"rejected"
        // Migrăm automat la format nou JSON
        if (consent === 'accepted' || consent === 'rejected') {
          const migrated = {
            necessary: true,
            statistics: consent === 'accepted',
            marketing: consent === 'accepted',
            preferences: consent === 'accepted'
          };
          localStorage.setItem('cookieConsent', JSON.stringify(migrated));
          setCookieConsent(migrated);
        } else {
          console.error('Error parsing cookie consent:', error);
        }
      }
    }

    // Listen pentru modificări cookie consent (când user acceptă/refuză)
    const handleStorageChange = (e) => {
      if (e.key === 'cookieConsent' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          setCookieConsent(parsed);
        } catch (error) {
          console.error('Error parsing cookie consent:', error);
        }
      }
    };

    // Listen pentru event custom din CookieBanner (același tab)
    const handleConsentChanged = () => {
      const consent = localStorage.getItem('cookieConsent');
      if (consent) {
        try {
          const parsed = JSON.parse(consent);
          setCookieConsent(parsed);
        } catch (error) {
          console.error('Error parsing cookie consent:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cookieConsentChanged', handleConsentChanged);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cookieConsentChanged', handleConsentChanged);
    };
  }, []);

  // Opt-out oficial GA4 pe /admin: dacă gtag e deja încărcat (navigare SPA din site spre admin),
  // `ga-disable-<ID>` = true oprește orice trimitere; revine la false la ieșirea din admin.
  useEffect(() => {
    const id = tracking?.googleAnalytics?.measurementId;
    if (id) window[`ga-disable-${id}`] = isAdminPage;
  }, [isAdminPage, tracking]);

  // Injectare Google Analytics 4
  useEffect(() => {
    if (window.location.pathname.startsWith('/admin')) return; // nu încărcăm GA4 pe admin
    if (!tracking?.googleAnalytics?.enabled) return;
    if (!tracking.googleAnalytics.measurementId) return;
    if (!cookieConsent?.statistics) return;

    const measurementId = tracking.googleAnalytics.measurementId;

    // Verifică dacă GA4 e deja încărcat
    if (window.gtag) return;

    // Injectare script GA4
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;
    script.id = 'ga4-script';
    document.head.appendChild(script);

    // Inițializare gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', measurementId);

    // Cleanup la unmount
    return () => {
      const existingScript = document.getElementById('ga4-script');
      if (existingScript) {
        existingScript.remove();
      }
      delete window.gtag;
      delete window.dataLayer;
    };
  }, [tracking, cookieConsent]);

  // Injectare Google Tag Manager
  useEffect(() => {
    if (window.location.pathname.startsWith('/admin')) return; // nu încărcăm GTM pe admin
    if (!tracking?.googleTagManager?.enabled) return;
    if (!tracking.googleTagManager.containerId) return;
    if (!cookieConsent?.statistics) return;

    const containerId = tracking.googleTagManager.containerId;

    // Verifică dacă GTM e deja încărcat
    if (window.google_tag_manager) return;

    // Injectare GTM <head> script
    const scriptHead = document.createElement('script');
    scriptHead.id = 'gtm-script-head';
    scriptHead.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `;
    document.head.appendChild(scriptHead);

    // Injectare GTM <body> noscript iframe
    const noscript = document.createElement('noscript');
    noscript.id = 'gtm-noscript-body';
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(noscript, document.body.firstChild);

    // Cleanup la unmount
    return () => {
      const existingScriptHead = document.getElementById('gtm-script-head');
      const existingNoscript = document.getElementById('gtm-noscript-body');
      if (existingScriptHead) existingScriptHead.remove();
      if (existingNoscript) existingNoscript.remove();
      delete window.google_tag_manager;
    };
  }, [tracking, cookieConsent]);

  // Componenta nu renderează nimic (doar side effects)
  return null;
};

export default GoogleTracking;
